"use client";

import type { CSSProperties } from "react";

import { cn } from "@/shared/lib/utils";

export const PRO_NICK_DEFAULT_COLOR_A = "#9333ea";
export const PRO_NICK_DEFAULT_COLOR_B = "#06b6d4";

type ProStyle = 0 | 1 | 2 | 3;

function resolveColors(a?: string | null, b?: string | null) {
  const hexOk = (s: string) => /^#[0-9A-Fa-f]{6}$/.test(s);
  return {
    ca: a && hexOk(a) ? a : PRO_NICK_DEFAULT_COLOR_A,
    cb: b && hexOk(b) ? b : PRO_NICK_DEFAULT_COLOR_B,
  };
}

export interface ProStyledDisplayNameProps {
  displayName: string;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
  className?: string;
  as?: "span" | "div";
}

export function ProStyledDisplayName({
  displayName,
  hasPro,
  proNicknameStyle = 0,
  proNicknameColorA,
  proNicknameColorB,
  className,
  as: Tag = "span",
}: ProStyledDisplayNameProps) {
  const st = proNicknameStyle as ProStyle;
  const { ca, cb } = resolveColors(proNicknameColorA, proNicknameColorB);

  if (!hasPro || st === 0) {
    return <Tag className={className}>{displayName}</Tag>;
  }

  const cssVars = {
    "--pro-nick-a": ca,
    "--pro-nick-b": cb,
  } as CSSProperties;

  if (st === 1) {
    return (
      <Tag
        className={cn(
          "pro-nick-gradient inline-block max-w-full bg-clip-text text-transparent",
          className,
        )}
        style={cssVars}
      >
        {displayName}
      </Tag>
    );
  }

  if (st === 2) {
    return (
      <Tag
        className={cn(
          "pro-nick-glow-pulse inline-block max-w-full",
          className,
        )}
        style={{
          ...cssVars,
          color: ca,
        }}
      >
        {displayName}
      </Tag>
    );
  }

  if (st === 3) {
    return (
      <Tag
        className={cn(
          "pro-nick-shine-wrap relative inline-block max-w-full overflow-hidden rounded-sm",
          className,
        )}
        style={{ ...cssVars, color: ca }}
      >
        <span className="relative z-1">{displayName}</span>
        <span
          className="pro-nick-shine-sweep pointer-events-none absolute inset-0 z-2"
          aria-hidden
        />
      </Tag>
    );
  }

  return <Tag className={className}>{displayName}</Tag>;
}
